import { c as _c } from "react/compiler-runtime";
import { IcoArrowTopRight16, IcoLock16, IcoMinusSmall16, IcoPlusSmall16 } from '@onefootprint/icons';
import { Dialog, IconButton, Tooltip } from '@onefootprint/ui';
import * as Collapsible from '@radix-ui/react-collapsible';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { InvestigationSourceDetail } from '../../hooks/use-sources';
import { formatSourceDate, humanizeDocumentIdentifier, splitUrl } from '../../utils/source-display';
import CitedPassages, { hasTextPassages, resolvedEvidence } from '../artifact-preview/components/cited-passages';
import SourceDetail from '../artifact-preview/components/source-detail';
import { labelSequence, SOURCE_BADGE_CLASS } from '../artifact-preview/source-citation-shared';
type SourcesDialogProps = {
  open: boolean;
  onClose: () => void;
  sources: InvestigationSourceDetail[];
  citedSourceLabels: ReadonlySet<string>;
  onOpenDocument?: (documentIdentifier: string) => void;
};

// A web page's body is its cited passages, so an uncited one has nothing to reveal and stays a plain
// link row. Every other kind always has something — a payload, a document, or the restricted notice.
const hasBody = (source: InvestigationSourceDetail) => source.data?.kind === 'web_fetch' ? hasTextPassages(resolvedEvidence(source)) : true;
const SourcesDialog = t0 => {
  const $ = _c(12);
  const {
    open,
    onClose,
    sources,
    citedSourceLabels,
    onOpenDocument
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "sourcesDialog"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("investigations", t1);
  let t2;
  if ($[1] !== t) {
    t2 = t(_temp);
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== citedSourceLabels || $[4] !== onOpenDocument || $[5] !== sources) {
    t3 = <SourcesList sources={sources} citedSourceLabels={citedSourceLabels} onOpenDocument={onOpenDocument} />;
    $[3] = citedSourceLabels;
    $[4] = onOpenDocument;
    $[5] = sources;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== onClose || $[8] !== open || $[9] !== t2 || $[10] !== t3) {
    t4 = <Dialog open={open} onClose={onClose} title={t2} size="large" noPadding={true}>{t3}</Dialog>;
    $[7] = onClose;
    $[8] = open;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
};
type SourcesListProps = Omit<SourcesDialogProps, 'open' | 'onClose'> & {
  /** Rows expanded on first render. Uncontrolled after that, like Radix's `defaultOpen`. */
  defaultOpenLabels?: Iterable<string>;
};

// Unlabelled sources sort last rather than colliding at 0.
const sortSequence = (label: string) => labelSequence(label) ?? Number.MAX_SAFE_INTEGER;
export const SourcesList = t0 => {
  const $ = _c(9);
  const {
    sources,
    citedSourceLabels,
    onOpenDocument,
    defaultOpenLabels
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "sourcesDialog"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("investigations", t1);
  const bySequence = _temp2;
  const cited = sources.filter(source => citedSourceLabels.has(source.label)).sort(bySequence);
  const uncited = sources.filter(source_0 => !citedSourceLabels.has(source_0.label)).sort(bySequence);
  let t2;
  if ($[1] !== defaultOpenLabels) {
    t2 = () => new Set(defaultOpenLabels);
    $[1] = defaultOpenLabels;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [openLabels, setOpenLabels] = useState(t2);
  const groups = [{
    key: "cited",
    heading: t(_temp3, {
      count: cited.length
    }),
    items: cited
  }, {
    key: "notCited",
    heading: t(_temp4, {
      count: uncited.length
    }),
    items: uncited
  }].filter(_temp5);
  const toggleSource = (label, nextOpen) => {
    setOpenLabels(previous => {
      const next = new Set(previous);
      if (nextOpen) {
        next.add(label);
      } else {
        next.delete(label);
      }
      return next;
    });
  };
  if (sources.length === 0) {
    let t3;
    if ($[3] !== t) {
      t3 = t(_temp6);
      $[3] = t;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== t3) {
      t4 = <div className="flex items-center justify-center px-4 py-10 text-body-3 text-tertiary">{t3}</div>;
      $[5] = t3;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    return t4;
  }
  const t3 = "flex flex-col gap-5 p-4";
  const t4 = groups.map(group_0 => <section key={group_0.key} className="flex flex-col gap-2"><h4 className="text-label-2 text-primary">{group_0.heading}</h4>{group_0.items.map(source_1 => <SourceRow key={source_1.label} source={source_1} open={openLabels.has(source_1.label)} onOpenChange={nextOpen_0 => toggleSource(source_1.label, nextOpen_0)} onOpenDocument={onOpenDocument} />)}</section>);
  let t5;
  if ($[7] !== t4) {
    t5 = <div className={t3}>{t4}</div>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
};

// A standalone row draws its own border and radius; inside the collapsible the container owns both,
// so a radius on the trigger would round the hover fill halfway down an expanded row.
const ROW_BASE = 'flex min-h-14 items-center gap-3 px-3 py-2 text-left';
const ROW_STANDALONE = `${ROW_BASE} rounded-default border border-tertiary bg-primary`;
const SourceLabel = t0 => {
  const $ = _c(2);
  const {
    label
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className={`${SOURCE_BADGE_CLASS} shrink-0 text-accent`}>{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const RowText = t0 => {
  const $ = _c(7);
  const {
    primary,
    secondary
  } = t0;
  let t1;
  if ($[0] !== primary) {
    t1 = <span className="block truncate text-label-3 text-primary">{primary}</span>;
    $[0] = primary;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== secondary) {
    t2 = secondary && <span className="block truncate text-body-3 text-tertiary">{secondary}</span>;
    $[2] = secondary;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <span className="min-w-0 flex-1 text-left">{t1}{t2}</span>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
type RowContent = {
  primary: React.ReactNode;
  secondary?: string;
  /** Only a fetched web page has somewhere to open — it's what puts the `↗` on the row. */
  link?: {
    url: string;
    host: string;
  };
};

// What the row reads as, per source kind. Web pages lead with the host so two pages on one site stay
// distinguishable by their muted path.
const useRowContent = source => {
  const $ = _c(33);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "sourcesDialog"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("investigations", t0);
  if (source.data?.kind === "web_fetch") {
    const {
      url,
      fetchedAt
    } = source.data.data;
    let t1;
    if ($[1] !== url) {
      t1 = splitUrl(url);
      $[1] = url;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    const {
      host,
      path
    } = t1;
    let t2;
    if ($[3] !== host || $[4] !== url) {
      t2 = {
        url,
        host
      };
      $[3] = host;
      $[4] = url;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    let t3;
    if ($[6] !== path) {
      t3 = path && <span className="text-tertiary">{path}</span>;
      $[6] = path;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    let t4;
    if ($[8] !== host || $[9] !== t3) {
      t4 = <>{host}{t3}</>;
      $[8] = host;
      $[9] = t3;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    let t5;
    if ($[11] !== fetchedAt || $[12] !== t) {
      t5 = t(_temp7, {
        date: formatSourceDate(fetchedAt)
      });
      $[11] = fetchedAt;
      $[12] = t;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    let t6;
    if ($[14] !== t2 || $[15] !== t4 || $[16] !== t5) {
      t6 = {
        link: t2,
        primary: t4,
        secondary: t5
      };
      $[14] = t2;
      $[15] = t4;
      $[16] = t5;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    return t6;
  }
  if (source.documentIdentifier) {
    let t1;
    if ($[18] !== source.documentIdentifier) {
      t1 = humanizeDocumentIdentifier(source.documentIdentifier);
      $[18] = source.documentIdentifier;
      $[19] = t1;
    } else {
      t1 = $[19];
    }
    let t2;
    if ($[20] !== t) {
      t2 = t(_temp8);
      $[20] = t;
      $[21] = t2;
    } else {
      t2 = $[21];
    }
    let t3;
    if ($[22] !== t1 || $[23] !== t2) {
      t3 = {
        primary: t1,
        secondary: t2
      };
      $[22] = t1;
      $[23] = t2;
      $[24] = t3;
    } else {
      t3 = $[24];
    }
    return t3;
  }
  if (!source.data) {
    const t1 = source.title;
    let t2;
    if ($[25] !== t) {
      t2 = t(_temp9);
      $[25] = t;
      $[26] = t2;
    } else {
      t2 = $[26];
    }
    let t3;
    if ($[27] !== source.title || $[28] !== t2) {
      t3 = {
        primary: t1,
        secondary: t2
      };
      $[27] = source.title;
      $[28] = t2;
      $[29] = t3;
    } else {
      t3 = $[29];
    }
    return t3;
  }
  let t1;
  if ($[30] !== source.subtitle || $[31] !== source.title) {
    t1 = {
      primary: source.title,
      secondary: source.subtitle
    };
    $[30] = source.subtitle;
    $[31] = source.title;
    $[32] = t1;
  } else {
    t1 = $[32];
  }
  return t1;
};
const SourceRow = t0 => {
  const $ = _c(48);
  const {
    source,
    open,
    onOpenChange,
    onOpenDocument
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "sourcesDialog"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("investigations", t1);
  const {
    primary,
    secondary,
    link
  } = useRowContent(source);
  const expandable = hasBody(source);
  const restricted = !source.data && !source.documentIdentifier;
  let t2;
  if ($[1] !== link || $[2] !== t) {
    t2 = link && <Tooltip content={t(_temp10)}><IconButton aria-label={t(_temp11, {
        name: link.host
      })} icon={IcoArrowTopRight16} className="max-md:size-9" size="tiny" variant="ghost" onClick={() => window.open(link.url, "_blank", "noopener,noreferrer")} /></Tooltip>;
    $[1] = link;
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const openSite = t2;
  let t3;
  if ($[4] !== source.label) {
    t3 = <SourceLabel label={source.label} />;
    $[4] = source.label;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== primary || $[7] !== secondary) {
    t4 = <RowText primary={primary} secondary={secondary} />;
    $[6] = primary;
    $[7] = secondary;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== restricted) {
    t5 = restricted && <IcoLock16 className="shrink-0" color="tertiary" aria-hidden={true} />;
    $[9] = restricted;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t4 || $[13] !== t5) {
    t6 = <>{t3}{t4}{t5}</>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const header = t6;
  if (!expandable) {
    let t7;
    if ($[15] !== header || $[16] !== openSite) {
      t7 = <div className={ROW_STANDALONE}>{header}{openSite}</div>;
      $[15] = header;
      $[16] = openSite;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    return t7;
  }
  let t7;
  if ($[18] !== open) {
    t7 = $_1 => open ? $_1.collapseSource : $_1.expandSource;
    $[18] = open;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== source.label || $[21] !== t || $[22] !== t7) {
    t8 = t(t7, {
      name: source.label
    });
    $[20] = source.label;
    $[21] = t;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const toggleLabel = t8;
  const t9 = open ? IcoMinusSmall16 : IcoPlusSmall16;
  let t10;
  if ($[24] !== t9 || $[25] !== toggleLabel) {
    t10 = <Collapsible.Trigger asChild={true}><IconButton aria-label={toggleLabel} icon={t9} className="max-md:size-9" size="tiny" variant="ghost" /></Collapsible.Trigger>;
    $[24] = t9;
    $[25] = toggleLabel;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== toggleLabel) {
    t11 = <Tooltip content={toggleLabel}>{t10}</Tooltip>;
    $[27] = t10;
    $[28] = toggleLabel;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== openSite) {
    t12 = openSite && <><span aria-hidden={true} className="h-4 w-px bg-(--border-color-tertiary)" />{openSite}</>;
    $[30] = openSite;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t11 || $[33] !== t12) {
    t13 = <span className="flex shrink-0 items-center gap-2">{t11}{t12}</span>;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== header || $[36] !== t13) {
    t14 = <div className={`${ROW_BASE} w-full`}>{header}{t13}</div>;
    $[35] = header;
    $[36] = t13;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== onOpenDocument || $[39] !== source) {
    t15 = <Collapsible.Content className="overflow-hidden data-[state=closed]:animate-collapseUp data-[state=open]:animate-collapseDown"><div className="border-t border-tertiary bg-primary"><SourceBody source={source} onOpenDocument={onOpenDocument} /></div></Collapsible.Content>;
    $[38] = onOpenDocument;
    $[39] = source;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== t14 || $[42] !== t15) {
    t16 = <div className="overflow-hidden rounded-default border border-tertiary bg-primary">{t14}{t15}</div>;
    $[41] = t14;
    $[42] = t15;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== onOpenChange || $[45] !== open || $[46] !== t16) {
    t17 = <Collapsible.Root open={open} onOpenChange={onOpenChange}>{t16}</Collapsible.Root>;
    $[44] = onOpenChange;
    $[45] = open;
    $[46] = t16;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  return t17;
};
const SourceBody = t0 => {
  const $ = _c(10);
  const {
    source,
    onOpenDocument
  } = t0;
  let t1;
  if ($[0] !== source) {
    t1 = resolvedEvidence(source);
    $[0] = source;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const evidences = t1;
  if (source.data?.kind === "web_fetch") {
    let t2;
    if ($[2] !== evidences || $[3] !== source.data.data.paragraphs) {
      t2 = <CitedPassages evidences={evidences} paragraphs={source.data.data.paragraphs} />;
      $[2] = evidences;
      $[3] = source.data.data.paragraphs;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t2;
  if ($[5] !== evidences || $[6] !== onOpenDocument || $[7] !== source.data || $[8] !== source.documentIdentifier) {
    t2 = <SourceDetail data={source.data} documentIdentifier={source.documentIdentifier} evidences={evidences} onOpenDocument={onOpenDocument} />;
    $[5] = evidences;
    $[6] = onOpenDocument;
    $[7] = source.data;
    $[8] = source.documentIdentifier;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
export default SourcesDialog;
function _temp($) {
  return $.title;
}
function _temp2(a, b) {
  return sortSequence(a.label) - sortSequence(b.label);
}
function _temp3($) {
  return $.citedWithCount;
}
function _temp4($_0) {
  return $_0.notCitedWithCount;
}
function _temp5(group) {
  return group.items.length > 0;
}
function _temp6($_1) {
  return $_1.empty;
}
function _temp7($) {
  return $.fetched;
}
function _temp8($_0) {
  return $_0.vaultDocument;
}
function _temp9($_1) {
  return $_1.restricted;
}
function _temp10($) {
  return $.openPage;
}
function _temp11($_0) {
  return $_0.opensInNewTab;
}